import { motion } from 'framer-motion';
import { FaSwimmer, FaTrophy, FaUsers, FaHeart, FaGraduationCap, FaHandsHelping, FaStar, FaClock, FaAward, FaBook } from 'react-icons/fa';
import { GiWhistle } from 'react-icons/gi';
import Seo from '../components/Seo';

function About() {
  const ethos = [
    {
      icon: <FaSwimmer className="text-4xl" />,
      title: 'Safe & Friendly Teaching',
      description: 'Teaching young children to swim in a safe and friendly environment',
      color: 'blue'
    },
    {
      icon: <FaGraduationCap className="text-4xl" />,
      title: 'Development Pathway',
      description: 'Teaching and coaching children through to team level where possible',
      color: 'green'
    },
    {
      icon: <FaStar className="text-4xl" />,
      title: 'Individual Potential',
      description: 'Assisting each individual to achieve their full potential as a competitive swimmer',
      color: 'purple'
    },
    {
      icon: <FaUsers className="text-4xl" />,
      title: 'Inclusive Approach',
      description: 'Recognition that not all members will be able to compete at first team level',
      color: 'orange'
    },
    {
      icon: <FaTrophy className="text-4xl" />,
      title: 'Team Focus',
      description: 'Team galas are of primary importance to the club',
      color: 'red'
    }
  ];

  const additionalAims = [
    {
      icon: <GiWhistle className="text-3xl" />,
      title: 'Qualifications',
      description: 'Provide opportunities for members and parents to acquire teaching and officiating qualifications'
    },
    {
      icon: <FaHeart className="text-3xl" />,
      title: 'Friendly Culture',
      description: 'Be a friendly and sociable club internally, friendly and sporting externally'
    },
    {
      icon: <FaHandsHelping className="text-3xl" />,
      title: 'Community Leadership',
      description: 'Play a leading role in the local community of swimming clubs'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'from-blue-50 to-blue-100', border: 'border-blue-500', icon: 'text-blue-600' },
      green: { bg: 'from-green-50 to-green-100', border: 'border-green-500', icon: 'text-green-600' },
      purple: { bg: 'from-purple-50 to-purple-100', border: 'border-purple-500', icon: 'text-purple-600' },
      orange: { bg: 'from-orange-50 to-orange-100', border: 'border-orange-500', icon: 'text-orange-600' },
      red: { bg: 'from-primary-50 to-primary-100', border: 'border-primary-500', icon: 'text-primary-600' }
    };
    return colors[color];
  };

  return (
    <main className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <Seo
        title="About Us"
        description="Learn about Marple Swimming Club's history since 1933, our mission, and the values that make us a friendly, competitive swimming club in Stockport."
        path="/about"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-display text-5xl font-bold text-navy-900 mb-6">About Marple Swimming Club</h1>
          <div className="max-w-4xl mx-auto space-y-4 text-lg text-gray-700 leading-relaxed">
            <p>
              Marple Swimming Club is a competitive, friendly and sociable swimming club. Whilst the new Marple pool is being built the club runs sessions at Romiley and New Mills swimming pools.
            </p>
            <p className="font-semibold text-primary-700">
              Our primary purpose is to train and maintain a competitive swimming team providing equal opportunity for all and an environment in which each swimmer can endeavour to realise their full potential.
            </p>
            <p>
              We cater for all age groups and abilities providing lessons for absolute beginner infants through to coaching sessions for senior competitive swimmers. Adult lane swimming sessions are also available.
            </p>
            <p className="text-base">
              We are an amateur club, buying pool time from the Council, run by a Committee of largely volunteer parents and Club members. Parents are encouraged to participate in any way they can to assist with the smooth running of the Club.
            </p>
          </div>
        </motion.div>

        {/* History Timeline */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-display text-4xl font-bold text-navy-900 text-center mb-12">Our History</h2>
          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-primary-300"></div>

            {/* Timeline items */}
            <div className="space-y-12">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="md:flex md:items-center">
                  <div className="md:w-1/2 md:pr-8 md:text-right">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-500 rounded-xl p-6 shadow-lg">
                      <h3 className="text-2xl font-bold text-blue-700 mb-2">1932</h3>
                      <p className="text-gray-800">Original Marple pool opens</p>
                    </div>
                  </div>
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white"></div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="md:flex md:items-center">
                  <div className="hidden md:block md:w-1/2"></div>
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-green-600 rounded-full border-4 border-white"></div>
                  <div className="md:w-1/2 md:pl-8">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-500 rounded-xl p-6 shadow-lg">
                      <h3 className="text-2xl font-bold text-green-700 mb-2">1933</h3>
                      <p className="text-gray-800">Marple Swimming Club is formed</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="md:flex md:items-center">
                  <div className="md:w-1/2 md:pr-8 md:text-right">
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-500 rounded-xl p-6 shadow-lg">
                      <h3 className="text-2xl font-bold text-purple-700 mb-2">1940s</h3>
                      <p className="text-gray-800">Break during war years</p>
                    </div>
                  </div>
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-purple-600 rounded-full border-4 border-white"></div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="md:flex md:items-center">
                  <div className="hidden md:block md:w-1/2"></div>
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-orange-600 rounded-full border-4 border-white"></div>
                  <div className="md:w-1/2 md:pl-8">
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-500 rounded-xl p-6 shadow-lg">
                      <h3 className="text-2xl font-bold text-orange-700 mb-2">1950s-2019</h3>
                      <p className="text-gray-800">Continuous operation and development of club swimmers at local, regional, national, and international levels</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="md:flex md:items-center">
                  <div className="md:w-1/2 md:pr-8 md:text-right">
                    <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-500 rounded-xl p-6 shadow-lg">
                      <h3 className="text-2xl font-bold text-red-700 mb-2">2019</h3>
                      <p className="text-gray-800">Original pool closes for redevelopment</p>
                    </div>
                  </div>
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-red-600 rounded-full border-4 border-white"></div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="md:flex md:items-center">
                  <div className="hidden md:block md:w-1/2"></div>
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-600 rounded-full border-4 border-white shadow-lg animate-pulse"></div>
                  <div className="md:w-1/2 md:pl-8">
                    <div className="bg-gradient-to-br from-primary-50 to-primary-100 border-2 border-primary-500 rounded-xl p-6 shadow-lg">
                      <h3 className="text-2xl font-bold text-primary-700 mb-2">Coming Soon!</h3>
                      <p className="text-gray-800 font-semibold">New Marple Community Hub with modern pool facilities</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Historical Resources Link */}
          <div className="mt-8 text-center">
            <a
              href="https://www.marplelocalhistorysociety.org.uk/our-local-heritage/marple-baths.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-bold hover:bg-blue-700 transition-all duration-300 shadow-lg"
            >
              <FaBook />
              View Historic Photos of Marple Baths
            </a>
            <p className="text-sm text-gray-600 mt-2">
              Courtesy of Marple Local History Society
            </p>
          </div>

          {/* Optional: Placeholder for future historical images */}
          {/*
          To add historical images to the timeline:
          1. Contact Marple Local History Society for permission
          2. Save images to /public/history/ folder
          3. Uncomment the image sections in the timeline items above
          4. Add image URLs like: /history/marple-pool-1932.jpg

          Example for 1932 timeline item:
          <img
            src="/history/marple-pool-1932.jpg"
            alt="Marple Pool 1932"
            className="w-full rounded-lg shadow-md mt-4"
          />
          */}
        </motion.section>

        {/* Club Ethos */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-display text-4xl font-bold text-navy-900 text-center mb-4">Our Club Ethos</h2>
          <p className="text-center text-gray-700 mb-8 max-w-3xl mx-auto">
            Marple Swimming Club is a competitive swimming club. Vital to our mission are the following principles:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {ethos.map((item, index) => {
              const colors = getColorClasses(item.color);
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-gradient-to-br ${colors.bg} border-2 ${colors.border} rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}
                >
                  <div className={`${colors.icon} mb-4`}>
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="bg-primary-50 border-2 border-primary-300 rounded-xl p-6 text-center">
            <p className="text-lg font-semibold text-primary-800">
              <FaHeart className="inline mr-2 text-primary-600" />
              Parents' Role: To encourage and assist all members to fulfil their swimming potential
            </p>
          </div>
        </motion.section>

        {/* Additional Aims */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="font-display text-3xl font-bold text-navy-900 text-center mb-8">Additional Aims</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {additionalAims.map((aim, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg border-2 border-gray-200 hover:border-primary-500 transition-all duration-300"
              >
                <div className="text-primary-600 mb-4">
                  {aim.icon}
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{aim.title}</h4>
                <p className="text-gray-700">{aim.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Coaching Team */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-display text-4xl font-bold text-navy-900 text-center mb-8">Teachers and Coaches</h2>
          <div className="max-w-4xl mx-auto space-y-4 text-gray-700 leading-relaxed text-center mb-8">
            <p>
              Our Head Coaches <strong className="text-primary-700">Lucy and Dan</strong> lead our experienced coaching team. All our teachers and coaches hold Amateur Swimming Association teaching qualifications, or the equivalent, and are CRB checked.
            </p>
            <p>
              They are assisted by poolside and in the water helpers - experienced swimmers from the Club who are encouraged and supported to achieve ASA qualifications.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-500 rounded-xl p-6 shadow-lg"
            >
              <div className="flex items-center gap-3 mb-4">
                <FaClock className="text-3xl text-blue-600" />
                <h3 className="text-2xl font-bold text-blue-700">Volunteer Development Pathway</h3>
              </div>
              <div className="space-y-3 text-gray-800">
                <p><strong className="text-blue-700">Age 14:</strong> Can volunteer as poolside helpers, often using this as foundation for Duke of Edinburgh Award</p>
                <p><strong className="text-blue-700">Age 16:</strong> Complete Swim England safeguarding course and DBS check</p>
                <p><strong className="text-blue-700">Qualifications:</strong> SEQ Level 1 Swimming Assistant and SEQ Level 2 Teaching Swimming qualifications</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-500 rounded-xl p-6 shadow-lg"
            >
              <div className="flex items-center gap-3 mb-4">
                <FaAward className="text-3xl text-green-600" />
                <h3 className="text-2xl font-bold text-green-700">Recent Achievements</h3>
              </div>
              <p className="text-gray-800 leading-relaxed">
                Congratulations to <strong className="text-green-700">Imogen, Beth, Abby, Emily and Harry</strong> who achieved SEQ qualifications in 2025. We look forward to seeing more Level 1 and 2 achievements in 2026!
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Swimming Groups Overview */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 shadow-lg"
        >
          <h2 className="font-display text-4xl font-bold text-primary-700 text-center mb-6">Swimming Groups</h2>
          <div className="max-w-3xl mx-auto space-y-4 text-gray-800 leading-relaxed mb-8">
            <p>
              There are classes for absolute Beginners which usually take children from around the age of four or five. At the appropriate time, these swimmers will usually feed into the Improvers group and then on to the intermediate Development group.
            </p>
            <p>
              When swimmers reach the required level of competence in all strokes they will move into the competitive Juniors group and then, as they progress, into the Seniors. Entry into these groups is dependent on ability and not age.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-primary-700 mb-4 flex items-center gap-2">
                <FaClock className="text-primary-600" />
                Training Schedule
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Beginners and Improvers:</strong> One lesson each week</li>
                <li><strong>Development:</strong> Two swimming sessions per week</li>
                <li><strong>Juniors and Seniors:</strong> Three training sessions each week</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-primary-700 mb-4 flex items-center gap-2">
                <FaUsers className="text-primary-600" />
                Waiting List
              </h3>
              <p className="text-gray-700">
                Due to the popularity of swimming lessons, the Club operates a waiting list when necessary to ensure classes are not overcrowded. Please use the contact form for availability information.
              </p>
            </div>
          </div>
        </motion.section>
      </div>
    </main>
  );
}

export default About;
